import { useState, useEffect, useRef } from 'react';

/**
 * Throttles streaming text for smooth ReactMarkdown rendering.
 *
 * Instead of character-by-character reveal (which causes 60fps re-renders
 * and visible flicker), this batches SSE chunks and flushes the full
 * accumulated text to React state at ~20fps. ReactMarkdown re-parses
 * at most 20 times per second — smooth, no flicker, no layout thrashing.
 */
export function useTypewriter(targetText: string, isActive: boolean): string {
  const [displayed, setDisplayed] = useState('');
  const latestRef = useRef('');
  const rafRef = useRef(0);
  const lastFlush = useRef(0);

  // Always track the latest incoming text without triggering renders
  latestRef.current = targetText;

  useEffect(() => {
    if (!isActive) {
      // Stream ended — sync immediately and stop the loop
      cancelAnimationFrame(rafRef.current);
      setDisplayed(targetText);
      return;
    }

    // New stream starting — reset
    setDisplayed('');
    lastFlush.current = 0;

    const INTERVAL = 50; // ~20fps — sweet spot for markdown rendering

    const tick = () => {
      const now = performance.now();
      const latest = latestRef.current;

      if (latest && now - lastFlush.current >= INTERVAL) {
        setDisplayed(latest);
        lastFlush.current = now;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  return displayed;
}
